/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { api } from '@/api/client'
import { OfflineError } from '@/lib/offline-error'
import { isDesktop } from '@/lib/platform'
import type { User, SetupStatus } from '@/api/types'

interface AuthContextType {
  user: User | null
  setupStatus: SetupStatus | null
  loading: boolean
  isAuthenticated: boolean
  needsSetup: boolean
  logout: () => void
  refreshUser: () => Promise<void>
  completeOnboarding: (uiMode?: string) => void
  resetOnboarding: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [setupStatus, setSetupStatus] = useState<SetupStatus | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshUser = useCallback(async () => {
    if (api.isAuthenticated()) {
      try {
        const me = await api.getMe()
        setUser(me)
        // Start the heartbeat timer (leader election handles multi-tab)
        api.startHeartbeat()
        // Warm the offline cache in the background after successful auth
        api.warmCache().catch(() => { /* non-critical */ })
      } catch (err) {
        // Don't clear the token on network errors - the session is still
        // valid, we just can't reach the API right now.
        if (err instanceof OfflineError || OfflineError.isNetworkError(err)) {
          // Leave token intact so offline UI can render
          return
        }
        api.clearToken()
        setUser(null)
      }
    }
  }, [])

  // Check initial state
  // Retry fetching setup status when API is unreachable.
  // Backoff: 0.5s for first 2s, then 1s for next 8s, then every 5s.
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const retryStartRef = useRef<number>(0)

  useEffect(() => {
    return () => {
      if (retryRef.current) clearTimeout(retryRef.current)
    }
  }, [])

  const scheduleRetry = useCallback(() => {
    if (retryRef.current) return // already scheduled
    if (!retryStartRef.current) retryStartRef.current = Date.now()
    const elapsed = Date.now() - retryStartRef.current
    const delay = elapsed < 2_000 ? 500 : elapsed < 10_000 ? 1_000 : 5_000
    retryRef.current = setTimeout(async () => {
      retryRef.current = null
      try {
        const status = await api.getSetupStatus()
        setSetupStatus(status)
        retryStartRef.current = 0
        if (api.isAuthenticated()) {
          await refreshUser()
        }
      } catch {
        // Still unreachable - keep retrying
        scheduleRetry()
      }
    }, delay)
  }, [refreshUser])

  useEffect(() => {
    const init = async () => {
      try {
        // Always check setup status
        const status = await api.getSetupStatus()
        setSetupStatus(status)

        // If we have a token, try to load the user
        if (api.isAuthenticated()) {
          await refreshUser()
        }
      } catch {
        // API not reachable - if we have a stored token, assume setup
        // is complete (we couldn't have obtained a token otherwise).
        // This prevents genesis/provisioning errors on offline reload.
        if (api.isAuthenticated()) {
          setSetupStatus({ needs_setup: false, provisioned: true } as SetupStatus)
          await refreshUser()
        }
        // Otherwise setupStatus stays null → genesis screen shows.
        // Keep retrying in the background so it auto-resolves.
        scheduleRetry()
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [refreshUser, scheduleRetry])

  const logout = useCallback(async () => {
    // Revoke the server-side session so the JWT can't be replayed
    await api.serverLogout()
    // On desktop, also revoke the device token and clear local storage
    if (isDesktop() && (window as any).ledgerDesktop?.getDeviceToken) {
      try {
        const dt = await (window as any).ledgerDesktop.getDeviceToken()
        if (dt) await api.deviceAuthRevoke(dt)
      } catch { /* best-effort */ }
      try {
        await (window as any).ledgerDesktop.clearDeviceToken()
      } catch { /* best-effort */ }
    }
    api.logout()
    setUser(null)
  }, [])

  const completeOnboarding = useCallback((uiMode?: string) => {
    setUser((prev) => prev ? { ...prev, onboarded: true, ...(uiMode ? { effective_ui_mode: uiMode } : {}) } : prev)
    api.completeOnboarding(true).catch(() => { /* fire-and-forget */ })
  }, [])

  const resetOnboarding = useCallback(() => {
    setUser((prev) => prev ? { ...prev, onboarded: false } : prev)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        setupStatus,
        loading,
        isAuthenticated: !!user,
        needsSetup: setupStatus?.needs_setup ?? true,
        logout,
        refreshUser,
        completeOnboarding,
        resetOnboarding,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
